import { debugRequested, isDevelopmentHost } from "./analytics";
import { loadScript } from "./load-script";

/**
 * Google Tag Manager, loaded without an inline `<script>`. Landing page only —
 * /officialrules is deliberately left out, at the client's request.
 *
 * Requested by the client. Google's snippet is inline script, which would mean
 * putting `'unsafe-inline'` back on `script-src` (see CLAUDE.md), so this does
 * what the snippet does from module code: push `gtm.start` onto the shared
 * `dataLayer`, then fetch gtm.js through `loadScript`. The `<noscript>` half of
 * the snippet is plain markup and lives in index.html as given.
 *
 * What runs INSIDE the container is configured in GTM, not here, and the CSP
 * still applies to it: a Custom HTML tag or a custom JavaScript variable is
 * blocked, and every new vendor origin has to be added to domains.config.json
 * (and to the production host's CSP) before its tag can load. A GA4 tag in the
 * container for G-LPB2PP8S12 would double-count every page view against
 * `initAnalytics` — that property is already sent from lib/analytics.ts.
 */

const CONTAINER_ID = "GTM-WSKSL66W";

const GTM_SRC = `https://www.googletagmanager.com/gtm.js?id=${CONTAINER_ID}`;

let started = false;

/**
 * Starts GTM for the current page. Idempotent, and skipped on the same
 * development hosts as GA4 (`?ga_debug=1` opts in there too), so local page
 * views never reach whatever tags the container carries.
 */
export function initTagManager(): void {
  if (typeof window === "undefined" || started) return;
  started = true;

  if (!debugRequested() && isDevelopmentHost(window.location.hostname)) {
    console.info(`[valvoline-gtm] skipped on ${window.location.hostname}`);
    return;
  }

  // A plain object, unlike gtag's `arguments` entries: GTM reads this event to
  // start its clock. Both share the queue, which is how Google ships them.
  (window.dataLayer ??= []).push({ "gtm.start": Date.now(), event: "gtm.js" });

  void loadScript(GTM_SRC).catch((error: unknown) => {
    console.info("[valvoline-gtm] gtm.js did not load:", error);
  });
}
